import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  applyColorScheme,
  getIsDarkFromDocument,
  getStoredColorScheme,
  subscribeColorScheme,
  type ColorScheme,
} from "@/utils/colorScheme";

type ColorSchemeContextValue = {
  colorScheme: ColorScheme;
  isDark: boolean;
  setColorScheme: (colorScheme: ColorScheme) => void;
  toggleColorScheme: () => void;
};

const ColorSchemeContext = createContext<ColorSchemeContextValue | null>(null);

export function ColorSchemeProvider({ children }: { children: ReactNode }) {
  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(() =>
    getStoredColorScheme(),
  );

  useEffect(() => subscribeColorScheme(setColorSchemeState), []);

  const setColorScheme = useCallback((next: ColorScheme) => {
    applyColorScheme(next);
    setColorSchemeState(next);
  }, []);

  const toggleColorScheme = useCallback(() => {
    setColorScheme(colorScheme === "dark" ? "light" : "dark");
  }, [colorScheme, setColorScheme]);

  const value = useMemo(
    () => ({
      colorScheme,
      isDark: colorScheme === "dark",
      setColorScheme,
      toggleColorScheme,
    }),
    [colorScheme, setColorScheme, toggleColorScheme],
  );

  return (
    <ColorSchemeContext.Provider value={value}>
      {children}
    </ColorSchemeContext.Provider>
  );
}

export function useColorScheme(): ColorSchemeContextValue {
  const context = useContext(ColorSchemeContext);
  if (!context) {
    throw new Error("useColorScheme must be used within ColorSchemeProvider");
  }
  return context;
}

/** Like useColorScheme but falls back to the document class outside a provider (tests, catalog, isolated renders). */
export function useColorSchemeWithFallback(): ColorSchemeContextValue {
  const context = useContext(ColorSchemeContext);
  // Start from the server-rendered scheme: hydration keeps the server markup, so a light
  // first render would never patch the dark-only classes. The effect then reads the document.
  const [fallbackScheme, setFallbackScheme] = useState<ColorScheme>("dark");

  useEffect(() => {
    if (context) return;
    setFallbackScheme(getIsDarkFromDocument() ? "dark" : "light");
    return subscribeColorScheme(setFallbackScheme);
  }, [context]);

  if (context) {
    return context;
  }

  return {
    colorScheme: fallbackScheme,
    isDark: fallbackScheme === "dark",
    setColorScheme: () => undefined,
    toggleColorScheme: () => undefined,
  };
}
